import React from "react";
import {
  CopilotProvider,
  clearActiveConversationId,
  readActiveConversationId,
  useCopilot,
  writeActiveConversationId,
} from "@oppizi-ecosystem/copilot";
// eslint-disable-next-line import/no-unresolved -- exports-map subpath; webpack resolves it, the eslint resolver can't read `exports`
import "@oppizi-ecosystem/copilot/styles.css";
import { usePostHog, useFeatureFlagEnabled, useFeatureFlagVariantKey } from "posthog-js/react";

import { COPILOT_PANEL_EVENTS } from "campaign-builder/analytics/cbAnalytics";

import { authorization } from "../module/auth";
import {
  createClientConversation,
  fetchClientSession,
  getConversationId,
  listClientConversations,
  primeClientSession,
} from "./api";
import { DEFAULT_SESSION_KEY, resolveClientSession } from "./conversationApi.mjs";
import { copilotCookieDomain } from "./cookieDomain";
import { emitAgentEvent } from "./agentEvents";
import { sharedCookieDomain } from "campaign-builder/sharedCookieDomain";
import { requestSidebarOpen } from "campaign-builder/sidebarOpenBus";
import { writeSidebarOpenCookie } from "campaign-builder/sidebarOpenCookie";
import { createPanelTelemetry } from "./panelTelemetry.mjs";
import UiControlMount from "./UiControlMount";

/**
 * Provider gate for the shared @oppizi-ecosystem/copilot chat. Mounted once inside
 * <Router> so it wraps every route header, whose <Copilot> launcher drives this
 * single panel through context.
 *
 * ALWAYS rendered (never a conditional wrapper): the element type at this slot never
 * changes, so the route tree is never remounted on an auth transition and a launcher
 * can never render without a provider ancestor (the shipped <Copilot> throws
 * otherwise). It is only "live" for authenticated clients — for everyone else it
 * mounts with empty session props, so the panel no-ops its TalkJS effect and the
 * launcher, gated on the same isClient() check, is not rendered. Idle auto-open +
 * PostHog tracking are built into the library (autoOpen / onAutoOpen).
 */
// ?copilot=open deep-link: the agent appends it to builder links it sends in
// chat, so the user lands with the panel already open and the conversation
// continuing. Effect-based (not defaultOpen) so SSR/hydration timing can't
// swallow the initial state: after mount, open through the imperative handle.
// Domain of the shared `sidebar_open` cookie, as ThemedOadsSidebar writes it
// ('' on localhost / previews → host-only). Evaluated once: sharedCookieDomain()
// reads window.location, stable per load.
const SIDEBAR_COOKIE_DOMAIN = sharedCookieDomain() || undefined;
// Domain of the library's own cookies (open state + selected conversation):
// 'oppizi.com' on oppizi.com hosts so both survive the hop to platform-next,
// undefined (host-only) on localhost / previews. Same per-load stability.
const COPILOT_COOKIE_DOMAIN = copilotCookieDomain();

const OpenOnDeepLink = ({ enabled }) => {
  const { open } = useCopilot();
  const fired = React.useRef(false);
  React.useEffect(() => {
    if (!enabled || fired.current) return;
    if (new URLSearchParams(window.location.search).get("copilot") !== "open") return;
    fired.current = true;
    open();
  }, [enabled, open]);
  return null;
};

// Agent-driven navigation (hidden `navigate` events posted by the dispatcher
// after an agent reply). Defense-in-depth re-validation of what the server
// already allowlisted: https + oppizi hosts only. `copilot=open` is appended
// so the destination loads with the chat panel open and the conversation
// continuing.
const NAVIGATE_HOSTS = new Set(["builder.oppizi.com", "next.oppizi.com", "app.oppizi.com"]);
const followAgentNavigation = (payload, onNavigate) => {
  const raw = payload && typeof payload.url === "string" ? payload.url : "";
  if (!raw) return false;
  let url;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" || !NAVIGATE_HOSTS.has(url.hostname)) return false;
  if (!url.searchParams.has("copilot")) url.searchParams.set("copilot", "open");
  onNavigate?.(url.toString());
  window.location.assign(url.toString());
  return true;
};

// Ag11 "widget closes itself" diagnosis: a full page load is the one way the
// panel can vanish WITHOUT the library reporting a close (persistOpen is off,
// so it comes back closed). `pagehide` — not `beforeunload` — fires reliably,
// including into bfcache and on this app's own `window.location.replace` hop
// to Creative Studio (DesignManagementRouter). Lives inside the provider so it
// can ask the library's imperative `isOpen()` rather than trust local state.
const PanelUnloadTelemetry = ({ telemetry }) => {
  const { isOpen } = useCopilot();
  React.useEffect(() => {
    const onPageHide = (event) => telemetry.onPageHide(event, isOpen);
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [telemetry, isOpen]);
  return null;
};

const ClientChat = ({ children }) => {
  const enabled = authorization.isClient();
  // An agent's request to drive arrives as server state, not as a message
  // payload, so the mount is nudged to re-check whenever the agent says
  // anything — which is exactly when a request can have appeared.
  const controlRef = React.useRef(null);
  const posthogTrack = usePostHog();
  // client-copilot-v2 refetch-sync kill switch. Two transports, both behind
  // the same flag and coalesced by useCopilotSync's debounce:
  //  - onAgentEvent (0.5.0): precise machine-readable events the agent posts
  //    as hidden messages (custom.event/eventPayload) — carries campaignId +
  //    kinds, so refetches are targeted.
  //  - onIncomingMessage fallback: any visible agent message → kinds:"all"
  //    for the open campaign, covering agents that don't post event messages.
  const refetchSyncEnabled = useFeatureFlagEnabled("copilot-refetch-sync");
  // Testers routed to the v2 agent (multivariate flag, email-targeted) get a
  // visible marker in the panel header so they know which agent they are on —
  // same flag and same title platform-next shows. Everyone else keeps the
  // library's default title.
  const agentVariant = useFeatureFlagVariantKey("talkjs_client_copilot_agent_slug");
  const isV2Tester = agentVariant === "client-copilot-v2";
  // Panel open/close/unload telemetry (Ag11). Ids, reasons, paths and
  // booleans only — see panelTelemetry.mjs.
  const panelTelemetry = React.useMemo(
    () =>
      createPanelTelemetry({
        posthog: posthogTrack,
        events: COPILOT_PANEL_EVENTS,
        host: "builder",
        getConversationId,
      }),
    [posthogTrack]
  );
  // Conversation history + switching, for every client (the backend half —
  // oppizi-api #7184: reopen-by-id, fresh threads, the list query — is live).
  // This used to sit behind a PostHog flag while the API deployed; the flag
  // is evaluated against the builder's own PostHog identity, so on the hop
  // from platform-next it could be off here while on there — the two hosts
  // then opened different threads and the cross-domain open state never
  // matched. No gate any more: platform-next has none either.
  const historyEnabled = enabled;
  // Which conversation the panel is on (library 2.6.0 `sessionKey`). The
  // selection lives in the `copilot_conversation` cookie, keyed on the user so
  // another account's pick on the shared domain never applies, and shared with
  // platform-next so a thread picked there is the one this page opens on.
  // 'default' = nothing stored → the backend's deterministic default thread.
  // Read in the state initializer (CRA: no SSR/hydration).
  // Always a STRING: the login payload carries a numeric id, platform-next
  // writes the cookie as String(user.id), and the library compares the two
  // strictly — a number here silently ignored every selection made on
  // next.oppizi.com (prod, 2026-09-17).
  const rawUserId = authorization.getUser()?.id;
  const userId = rawUserId != null && rawUserId !== "" ? String(rawUserId) : undefined;
  const readStoredSelection = React.useCallback(
    () => (historyEnabled && userId ? readActiveConversationId(userId) : null) ?? DEFAULT_SESSION_KEY,
    [historyEnabled, userId]
  );
  const [conversationId, setConversationId] = React.useState(readStoredSelection);
  React.useEffect(() => {
    if (!historyEnabled) return;
    const stored = readStoredSelection();
    if (stored !== DEFAULT_SESSION_KEY)
      setConversationId((current) => (current === DEFAULT_SESSION_KEY ? stored : current));
  }, [historyEnabled, readStoredSelection]);
  // README recipe (resolveClientSession, unit-tested): resolve THAT
  // conversation; only "Conversation not found" forgets the selection and
  // falls back to the default thread; an API without the reopen argument yet
  // falls back without forgetting; anything else rethrows so the library
  // retries / shows Retry. The forget is guarded like platform-next's: only
  // the id that failed is cleared, never a thread picked meanwhile in another
  // tab or on platform-next. The key is left as it is on purpose: changing it
  // inside fetchSession restarts the fetch cycle.
  const fetchSession = React.useCallback(
    () =>
      resolveClientSession({
        conversationId,
        fetchSession: fetchClientSession,
        forgetSelection: (failedId) => {
          if (userId && readActiveConversationId(userId) === failedId) clearActiveConversationId(COPILOT_COOKIE_DOMAIN);
        },
      }),
    [conversationId, userId]
  );
  const selectConversation = React.useCallback(
    (id) => {
      if (userId) writeActiveConversationId(id, { userId, domain: COPILOT_COOKIE_DOMAIN });
      setConversationId(id);
    },
    [userId]
  );
  // The panel's History toggle + "start new conversation" control. The
  // library renders the list and reports the pick; switching the panel onto
  // it is ours (sessionKey). `onNew` mints a real separate thread server-side
  // (fresh:true reuses the caller's most recent EMPTY thread, so mashing the
  // button doesn't litter) and then switches to it — never `!new`.
  const history = React.useMemo(
    () => ({
      list: listClientConversations,
      onSelect: selectConversation,
      // `onNew` is fire-and-forget for the library (`() => void`), so the
      // failure is logged here — message only, never the token — instead of
      // surfacing as an unhandled rejection; the panel simply stays where it is.
      onNew: () => {
        createClientConversation({ fresh: true })
          .then((created) => {
            primeClientSession(created);
            selectConversation(created.conversationId);
          })
          .catch((error) => {
            console.error("[Copilot] could not start a new conversation", error?.message ?? error);
          });
      },
    }),
    [selectConversation]
  );
  // The left menu collapses when the chat OPENS (Slava, 2026-09-17): launcher,
  // CTA or deep link — not the library's `restore`, which re-opens the panel on
  // every page load / platform-next hop while a chat is open and would drag a
  // menu the user re-expanded closed again each time (and flip the cookie
  // platform-next reads). The bus moves the sidebar mounted on this page, the
  // cookie write makes the next page — and platform-next — agree.
  const handleOpenChange = React.useCallback(
    (open, reason) => {
      if (open && reason !== "restore") {
        requestSidebarOpen(false);
        writeSidebarOpenCookie(false, { domain: SIDEBAR_COOKIE_DOMAIN });
      }
      panelTelemetry.onOpenChange(open, reason);
    },
    [panelTelemetry]
  );

  return (
    <CopilotProvider
      labels={isV2Tester ? { title: "Copilot v2 (beta)" } : undefined}
      // 1.0 API: the library resolves the session itself. `lazyLoad` keeps the
      // old "mounted but inert" behaviour for non-clients — the provider still
      // renders unconditionally (so the route tree never remounts and a
      // launcher always has an ancestor), but nothing is fetched until
      // something actually opens the panel, and for a non-client nothing can:
      // autoOpen is false and the launcher is not rendered.
      fetchSession={fetchSession}
      sessionKey={conversationId}
      history={historyEnabled ? history : undefined}
      // Ag11 (2026-09-16, verified in PostHog): the panel "closing itself" was
      // the PAGE reloading under it — an "Unloaded While Open" event on the
      // same path with no navigation and never a "Closed" event. persistOpen
      // restores an open panel after a same-origin load (per conversation, per
      // tab; an explicit close stays closed, so a browsing user is never
      // auto-opened). The library can only restore once it knows the
      // conversation id, which lazyLoad defers until the first open — so for
      // clients the session must resolve eagerly; non-clients keep lazyLoad
      // (nothing can open the panel for them, so nothing is ever fetched).
      lazyLoad={!enabled}
      persistOpen
      // The open state hops to platform-next (and back) through the
      // `copilot_panel_open` cookie on the shared domain instead of the
      // per-origin sessionStorage; host-only on localhost / previews.
      persistOpenDomain={COPILOT_COOKIE_DOMAIN}
      // Never open on its own (Slava, 2026-09-17): launcher, CTA, deep link
      // or restore only.
      autoOpen={false}
      onOpenChange={handleOpenChange}
      onAgentEvent={(event) => {
        if (event.name === "navigate" && followAgentNavigation(event.payload, panelTelemetry.noteOutgoingNavigation))
          return;
        if (!refetchSyncEnabled) return;
        emitAgentEvent(event);
      }}
      onIncomingMessage={({ senderId }) => {
        controlRef.current?.recheck();
        if (!refetchSyncEnabled) return;
        emitAgentEvent({
          name: "campaign_updated",
          payload: { kinds: "all" },
          senderId: senderId ?? null,
        });
      }}
    >
      <OpenOnDeepLink enabled={enabled} />
      <PanelUnloadTelemetry telemetry={panelTelemetry} />
      {enabled ? <UiControlMount ref={controlRef} /> : null}
      {children}
    </CopilotProvider>
  );
};

export default ClientChat;
